import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import {
  AUTO_LOGOUT_MAX,
  AUTO_LOGOUT_MIN,
  autoLogoutMinutesSchema,
  formatAutoLogout,
  type AutoLogoutOverview,
  type AutoLogoutUserRow,
} from '@ehr/shared';
import { api, ApiError } from '../lib/api.js';
import { useSession } from '../auth/session.js';
import { askConfirm, errorText, showError } from '../lib/dialogs.js';
import { timeOpts } from '../lib/timePrefs.js';
import { Dd } from './Dropdown.js';
import { Modal } from './Modal.js';
import './AutoLogoutSettings.css';

/**
 * ehr#416 - Data & settings > Auto logout (SupportCredify's ADMIN_SETTINGS_10-13-V1 mockup):
 * the practice default, each person's own time (several at once, or back to the default), and
 * the change history. A user's own time saves when it's applied; the practice default and client
 * portal times save with Data & settings' Save changes (ehr#583). Changes apply in every open tab
 * within 5 minutes (the signed-in person's own tab at once).
 */

const fmtWhen = (iso: string) =>
  new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...timeOpts(),
  });

/** The minutes picker: the practice's times plus Custom..., which opens a number box. */
function TimeField({
  presets,
  value,
  onChange,
  invalid,
  id,
}: {
  presets: number[];
  value: string;
  onChange: (v: string) => void;
  invalid: boolean;
  id: string;
}) {
  const isPreset = presets.map(String).includes(value);
  const [custom, setCustom] = useState(!isPreset && value !== '');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (custom) inputRef.current?.focus();
  }, [custom]);
  return (
    <div className={`alo-time${invalid ? ' invalid' : ''}`}>
      <Dd
        value={custom ? 'custom' : value}
        ariaLabel="Logout after"
        options={[
          ...presets.map((n) => ({ value: String(n), label: formatAutoLogout(n) })),
          { value: 'custom', label: 'Custom...' },
        ]}
        onChange={(v) => {
          if (v === 'custom') {
            setCustom(true);
            onChange('');
          } else {
            setCustom(false);
            onChange(v);
          }
        }}
      />
      {custom && (
        <input
          ref={inputRef}
          id={id}
          className="alo-custom"
          type="number"
          min={AUTO_LOGOUT_MIN}
          max={AUTO_LOGOUT_MAX}
          step={1}
          inputMode="numeric"
          placeholder={`Minutes (${AUTO_LOGOUT_MIN} to ${AUTO_LOGOUT_MAX})`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );
}

/** "" or text → minutes, or the reason it isn't one. */
function readMinutes(raw: string): { ok: true; value: number } | { ok: false; error: string } {
  if (raw.trim() === '') return { ok: false, error: 'Choose a time' };
  const r = autoLogoutMinutesSchema.safeParse(Number(raw));
  return r.success ? { ok: true, value: r.data } : { ok: false, error: r.error.issues[0]!.message };
}

const whoLabel = (users: AutoLogoutUserRow[]) =>
  users.length === 1 ? users[0]!.name : `${users.length} users`;

export function AutoLogoutSettings({
  canEdit,
  onPending,
}: {
  canEdit: boolean;
  /**
   * ehr#465 (Chase 10-16): a time picked but not saved yet. Data & settings counts it as unsaved and
   * its Save changes runs this (ehr#583: the only way to save these two times now). It resolves false
   * when a time isn't valid, the question was cancelled or the save failed. null when nothing waits.
   */
  onPending?: (save: (() => Promise<boolean>) | null) => void;
}) {
  const { user } = useSession();
  const [data, setData] = useState<AutoLogoutOverview | null>(null);
  const [loadErr, setLoadErr] = useState('');
  const [defaultRaw, setDefaultRaw] = useState('');
  const [defaultInvalid, setDefaultInvalid] = useState(false);
  // ehr#416: the client portal's one time
  const [clientRaw, setClientRaw] = useState('');
  const [clientInvalid, setClientInvalid] = useState(false);
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('');
  const [historyQ, setHistoryQ] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [modalIds, setModalIds] = useState<string[] | null>(null);
  const [modalRaw, setModalRaw] = useState('');
  const [modalInvalid, setModalInvalid] = useState('');
  const [ctx, setCtx] = useState<{ x: number; y: number } | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const historyRef = useRef<HTMLElement>(null);

  const apply = (d: AutoLogoutOverview) => {
    setData(d);
    setDefaultRaw(String(d.defaultMinutes));
    setClientRaw(String(d.clientMinutes));
  };
  useEffect(() => {
    if (!canEdit) return;
    api
      .get<AutoLogoutOverview>('/auto-logout')
      .then(apply)
      .catch((e: unknown) => setLoadErr(e instanceof ApiError ? e.message : 'Could not load.'));
  }, [canEdit]);

  // Close the right-click menu on any click elsewhere, a scroll or Escape.
  useEffect(() => {
    if (!ctx) return;
    const close = () => setCtx(null);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('mousedown', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [ctx]);

  const roles = useMemo(
    () => [...new Set((data?.users ?? []).map((u) => u.role))].sort(),
    [data?.users],
  );
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.users ?? []).filter(
      (u) =>
        (!role || u.role === role) &&
        (!q || `${u.name} ${u.email} ${u.role}`.toLowerCase().includes(q)),
    );
  }, [data?.users, search, role]);
  const history = useMemo(() => {
    const q = historyQ.trim().toLowerCase();
    return (data?.history ?? []).filter(
      (h) => !q || `${h.by} ${h.appliesTo} ${h.from} ${h.to}`.toLowerCase().includes(q),
    );
  }, [data?.history, historyQ]);

  // ehr#465: the 1 hour Chase picked was never saved (Save changes at the foot saved nothing and said
  // "No unsaved changes"); a pick that differs from the saved time is now an unsaved change. The hooks
  // sit above the early returns; the save itself is filled in below, once the card's saves exist.
  const defaultPending = !!data && defaultRaw !== String(data.defaultMinutes);
  const clientPending = !!data && clientRaw !== String(data.clientMinutes);
  const pending = defaultPending || clientPending;
  const savePendingRef = useRef<() => Promise<boolean>>(async () => true);
  useEffect(() => {
    onPending?.(pending ? () => savePendingRef.current() : null);
  }, [pending]);
  useEffect(() => () => onPending?.(null), []);

  if (!canEdit)
    return <p className="alo-note">Only an admin or manager can see and change auto logout.</p>;
  if (!data) return <p className="alo-note">{loadErr || 'Loading…'}</p>;

  const users = (ids: string[]) => data.users.filter((u) => ids.includes(u.id));
  const sel = [...selected].filter((id) => data.users.some((u) => u.id === id));
  const allVisibleOn = visible.length > 0 && visible.every((u) => selected.has(u.id));
  const someVisibleOn = visible.some((u) => selected.has(u.id));
  const toggle = (id: string, on: boolean) =>
    setSelected((s) => {
      const n = new Set(s);
      if (on) n.add(id);
      else n.delete(id);
      return n;
    });

  const save = async (path: string, body: unknown, done: string): Promise<boolean> => {
    setBusy(true);
    try {
      const r = await api.put<AutoLogoutOverview & { changed: number }>(path, body);
      apply(r);
      setNote(done);
      return true;
    } catch (e) {
      showError(errorText(e, 'Could not save the auto logout time.'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  // ehr#583 (Chase 10-21): no Save Default / Save Client Portal buttons. Save changes at the foot of
  // Data & settings saves both times, after one question that names what changes.
  savePendingRef.current = async () => {
    const asks: string[] = [];
    const puts: { path: string; body: unknown; done: string }[] = [];
    if (defaultPending) {
      const r = readMinutes(defaultRaw);
      if (!r.ok) {
        setDefaultInvalid(true);
        showError(`Practice Default: ${r.error}`);
        return false;
      }
      const n = data.users.filter((u) => u.timeoutMinutes == null).length;
      asks.push(
        `Change the practice default to "${formatAutoLogout(r.value)}"? ${n} ${
          n === 1 ? 'user follows' : 'users follow'
        } the practice default and will be signed out after ${formatAutoLogout(r.value)} with no activity.`,
      );
      puts.push({
        path: '/auto-logout/default',
        body: { defaultMinutes: r.value },
        done: `Practice default set to ${formatAutoLogout(r.value)}.`,
      });
    }
    // ehr#416 (Chase, 10-14): one time for every client in the client portal
    if (clientPending) {
      const r = readMinutes(clientRaw);
      if (!r.ok) {
        setClientInvalid(true);
        showError(`Client Portal: ${r.error}`);
        return false;
      }
      asks.push(
        `Change the client portal's logout time to "${formatAutoLogout(r.value)}"? Every client is signed out of the portal after ${formatAutoLogout(r.value)} with no activity.`,
      );
      puts.push({
        path: '/auto-logout/client',
        body: { clientMinutes: r.value },
        done: `Client portal set to ${formatAutoLogout(r.value)}.`,
      });
    }
    if (!puts.length) return true;
    const ok = await askConfirm({ message: asks.join(' '), confirmLabel: 'Save', danger: false });
    if (!ok) return false;
    for (const p of puts) if (!(await save(p.path, p.body, p.done))) return false;
    setNote(puts.map((p) => p.done).join(' '));
    return true;
  };

  const openSetTimeout = (ids: string[]) => {
    if (!ids.length) return;
    const us = users(ids);
    const same = us.every((u) => u.timeoutMinutes === us[0]!.timeoutMinutes);
    setModalRaw(String((same && us[0]!.timeoutMinutes) || data.defaultMinutes));
    setModalInvalid('');
    setModalIds(ids);
  };

  const applyModal = async () => {
    if (!modalIds) return;
    const r = readMinutes(modalRaw);
    if (!r.ok) {
      setModalInvalid(r.error);
      return;
    }
    const who = whoLabel(users(modalIds));
    const ids = modalIds;
    setModalIds(null);
    await save(
      '/auto-logout/users',
      { userIds: ids, timeoutMinutes: r.value },
      `${who} now ${ids.length === 1 ? 'signs' : 'sign'} out after ${formatAutoLogout(r.value)}.`,
    );
  };

  const useDefault = async (ids: string[]) => {
    const custom = users(ids).filter((u) => u.timeoutMinutes != null);
    if (!custom.length) {
      setNote(
        ids.length === 1
          ? `${users(ids)[0]?.name ?? 'This user'} already uses the practice default.`
          : 'These users already use the practice default.',
      );
      return;
    }
    const who = whoLabel(custom);
    const def = formatAutoLogout(data.defaultMinutes);
    const ok = await askConfirm({
      message: `Use the practice default for ${custom.length === 1 ? `"${who}"` : who}? Auto logout goes back to the practice default of ${def}.`,
      confirmLabel: 'Use Default',
      danger: false,
    });
    if (!ok) return;
    await save(
      '/auto-logout/users',
      { userIds: custom.map((u) => u.id), timeoutMinutes: null },
      `${who} now on the practice default (${def}).`,
    );
  };

  const viewHistory = (id: string) => {
    const u = data.users.find((x) => x.id === id);
    if (!u) return;
    setHistoryQ(u.name);
    historyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const onRowMenu = (e: MouseEvent, id: string) => {
    e.preventDefault();
    if (!selected.has(id)) setSelected(new Set([id]));
    setCtx({ x: e.clientX, y: e.clientY });
  };
  const ctxIds = sel.length ? sel : [];
  const modalUsers = modalIds ? users(modalIds) : [];

  return (
    // Ticks and searches here aren't Data & settings' "Unsaved changes" (its panes mark dirty on any
    // change that reaches them); the two times report through onPending instead (ehr#465, ehr#583).
    <div className="alo" onChange={(e) => e.stopPropagation()}>
      <section className="alo-card alo-default">
        <h4>Practice Default</h4>
        <span className="alo-lbl">Logout After</span>
        <TimeField
          id="alo-default-custom"
          presets={data.presets}
          value={defaultRaw}
          invalid={defaultInvalid}
          onChange={(v) => {
            setDefaultRaw(v);
            setDefaultInvalid(false);
          }}
        />
        {defaultPending && <span className="alo-unsaved">Not saved yet</span>}
      </section>

      {/* ehr#416 (Chase, 10-14): the client portal, one time for every client */}
      <section className="alo-card alo-default">
        <h4>Client Portal</h4>
        <span className="alo-lbl">Logout After</span>
        <TimeField
          id="alo-client-custom"
          presets={data.presets}
          value={clientRaw}
          invalid={clientInvalid}
          onChange={(v) => {
            setClientRaw(v);
            setClientInvalid(false);
          }}
        />
        {clientPending && <span className="alo-unsaved">Not saved yet</span>}
        <p className="alo-note">
          One time for every client. After it with no activity, the portal shows a 2:00 warning,
          then signs the client out.
        </p>
      </section>

      {note && (
        <p className="alo-done" role="status">
          {note}
        </p>
      )}

      <section className="alo-card">
        <div className="alo-head">
          <h4>Users</h4>
          <input
            className="alo-search"
            type="search"
            placeholder="Search name, email or role"
            aria-label="Search users"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="alo-role">
            <Dd
              value={role}
              ariaLabel="Role"
              options={[
                { value: '', label: 'All Roles' },
                ...roles.map((r) => ({ value: r, label: r })),
              ]}
              onChange={setRole}
            />
          </div>
          <button
            className="btn"
            onClick={() => {
              setSearch('');
              setRole('');
            }}
          >
            Clear
          </button>
        </div>
        <div className="alo-scroll">
          <table className="alo-tbl">
            <thead>
              <tr>
                <th className="alo-check">
                  <input
                    type="checkbox"
                    aria-label="Select all users"
                    checked={allVisibleOn}
                    ref={(el) => {
                      if (el) el.indeterminate = someVisibleOn && !allVisibleOn;
                    }}
                    onChange={(e) =>
                      setSelected((s) => {
                        const n = new Set(s);
                        for (const u of visible) {
                          if (e.target.checked) n.add(u.id);
                          else n.delete(u.id);
                        }
                        return n;
                      })
                    }
                  />
                </th>
                <th>Name</th>
                <th>Role</th>
                <th>Email</th>
                <th>Logout After</th>
                <th title="Default follows the practice default. Custom is set for that user.">
                  Setting
                </th>
                <th>Last Changed</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr>
                  <td colSpan={7} className="alo-empty">
                    No users match.
                  </td>
                </tr>
              )}
              {visible.map((u) => (
                <tr
                  key={u.id}
                  className={selected.has(u.id) ? 'on' : undefined}
                  onContextMenu={(e) => onRowMenu(e, u.id)}
                >
                  <td className="alo-check">
                    <input
                      type="checkbox"
                      aria-label={`Select ${u.name}`}
                      checked={selected.has(u.id)}
                      onChange={(e) => toggle(u.id, e.target.checked)}
                    />
                  </td>
                  <td>
                    <button className="alo-name" onClick={() => openSetTimeout([u.id])}>
                      {u.name}
                    </button>
                    {u.id === user?.id && <span className="alo-you"> (you)</span>}
                  </td>
                  <td>{u.role}</td>
                  <td className="alo-email">{u.email}</td>
                  <td>{formatAutoLogout(u.timeoutMinutes ?? data.defaultMinutes)}</td>
                  <td>{u.timeoutMinutes == null ? 'Default' : 'Custom'}</td>
                  <td>{u.lastChanged ? fmtWhen(u.lastChanged) : 'Never'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="alo-card" ref={historyRef}>
        <div className="alo-head">
          <h4>Change History</h4>
          <input
            className="alo-search"
            type="search"
            placeholder="Search history"
            aria-label="Search history"
            value={historyQ}
            onChange={(e) => setHistoryQ(e.target.value)}
          />
        </div>
        <div className="alo-scroll">
          <table className="alo-tbl">
            <thead>
              <tr>
                <th>Date</th>
                <th>Changed By</th>
                <th>Applies To</th>
                <th>From</th>
                <th>To</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 && (
                <tr>
                  <td colSpan={5} className="alo-empty">
                    {data.history.length ? 'No changes match.' : 'No changes yet.'}
                  </td>
                </tr>
              )}
              {history.map((h) => (
                <tr key={h.id}>
                  <td>{fmtWhen(h.at)}</td>
                  <td>{h.by}</td>
                  <td>{h.appliesTo}</td>
                  <td>{h.from}</td>
                  <td>{h.to}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="alo-bar">
        <span className="alo-count">
          {sel.length} of {data.users.length} users selected
        </span>
        <button
          className="btn"
          disabled={!sel.length || busy}
          title={sel.length ? undefined : 'Select users first'}
          onClick={() => void useDefault(sel)}
        >
          Use Default
        </button>
        <button
          className="btn primary"
          disabled={!sel.length || busy}
          title={sel.length ? undefined : 'Select users first'}
          onClick={() => openSetTimeout(sel)}
        >
          Set Timeout
        </button>
      </div>

      {ctx && ctxIds.length > 0 && (
        <div
          className="alo-ctx"
          role="menu"
          style={{
            left: Math.max(8, Math.min(ctx.x, window.innerWidth - 220)),
            top: Math.max(8, Math.min(ctx.y, window.innerHeight - 170)),
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="alo-ctx-head">
            {ctxIds.length === 1 ? users(ctxIds)[0]?.name : `${ctxIds.length} users selected`}
          </div>
          <button
            role="menuitem"
            onClick={() => {
              setCtx(null);
              openSetTimeout(ctxIds);
            }}
          >
            Set Timeout
          </button>
          <button
            role="menuitem"
            onClick={() => {
              setCtx(null);
              void useDefault(ctxIds);
            }}
          >
            Use Default
          </button>
          {ctxIds.length === 1 && (
            <button
              role="menuitem"
              onClick={() => {
                setCtx(null);
                viewHistory(ctxIds[0]!);
              }}
            >
              View History
            </button>
          )}
        </div>
      )}

      {modalIds && (
        <Modal
          title={
            modalUsers.length === 1
              ? `Set Timeout for ${modalUsers[0]!.name}`
              : `Set Timeout for ${modalUsers.length} Users`
          }
          className="alo-modal"
          onClose={() => setModalIds(null)}
          actions={
            <>
              <button className="btn" onClick={() => setModalIds(null)}>
                Cancel
              </button>
              <button className="btn primary" onClick={() => void applyModal()} disabled={busy}>
                Apply
              </button>
            </>
          }
        >
          <div className="alo-form-row">
            <span className="alo-lbl">Applies To</span>
            <div className="alo-applies">{modalUsers.map((u) => u.name).join(', ')}</div>
          </div>
          <div className="alo-form-row">
            <span className="alo-lbl">Logout After</span>
            <TimeField
              id="alo-modal-custom"
              presets={data.presets}
              value={modalRaw}
              invalid={!!modalInvalid}
              onChange={(v) => {
                setModalRaw(v);
                setModalInvalid('');
              }}
            />
            {modalInvalid && (
              <span className="alo-err" role="alert">
                {modalInvalid}
              </span>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
